
import { useMemo, useCallback } from "react";

export const useVideoUrlChecker = () => {
  return useCallback((url: string) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const hasVideoExtension = videoExtensions.some(ext => url.toLowerCase().includes(ext));
    const isPexelsVideo = url.includes('video-files') || url.includes('.mp4');
    const isDataVideoUrl = url.startsWith('data:video/');
    return hasVideoExtension || isPexelsVideo || isDataVideoUrl;
  }, []);
};

export const useCustomFont = (customFont?: string) => {
  return useMemo(() => {
    if (customFont) {
      const fontName = customFont.replace(/\+/g, ' ');
      return `font-['${fontName}']`;
    }
    return '';
  }, [customFont]);
};

export const createBackgroundStyle = (
  backgroundUrl?: string, 
  backgroundColor?: string | any,
  isVideoUrl?: (url: string) => boolean
): React.CSSProperties => {
  const styles: React.CSSProperties = {};
  
  // Handle background color first (as it might be a gradient)
  if (backgroundColor) {
    // Handle both string colors and object colors from form data
    const colorValue = typeof backgroundColor === 'object' && backgroundColor?.value 
      ? backgroundColor.value 
      : backgroundColor;
    
    if (typeof colorValue === 'string') {
      if (colorValue.startsWith('linear-gradient')) {
        styles.background = colorValue;
      } else {
        styles.backgroundColor = colorValue;
      }
    }
  }
  
  // Handle background image/video
  if (backgroundUrl) {
    if (backgroundUrl.startsWith('linear-gradient')) {
      // If it's a gradient, override any existing background
      styles.background = backgroundUrl;
      // Remove backgroundColor if it was set
      delete styles.backgroundColor;
    } else if (backgroundUrl.startsWith('#')) {
      // Handle hex colors - only set if no backgroundColor was already set
      if (!styles.backgroundColor) {
        styles.backgroundColor = backgroundUrl;
      }
    } else if (isVideoUrl && isVideoUrl(backgroundUrl)) {
      styles.position = 'relative';
    } else {
      // For image URLs, set individual properties
      styles.backgroundImage = `url(${backgroundUrl})`;
      styles.backgroundSize = 'cover';
      styles.backgroundPosition = 'center';
      styles.backgroundRepeat = 'no-repeat';
    }
  }
  
  return styles;
};

export const loadCustomFont = (customFont: string) => {
  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${customFont}:wght@400;500;600;700&display=swap`;
  link.rel = 'stylesheet';
  link.id = `custom-font-${customFont}`;
  
  const existingLink = document.getElementById(`custom-font-${customFont}`);
  if (existingLink) {
    existingLink.remove();
  }
  
  document.head.appendChild(link);
  
  return () => {
    const linkToRemove = document.getElementById(`custom-font-${customFont}`);
    if (linkToRemove) {
      linkToRemove.remove();
    }
  };
};
